import { AxiosResponse } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { all, AllEffect, call, CallEffect, ForkEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';
import {
  addItemsToCartFulfilled as addCartItemsFulfilled,
  addItemsToCartRejected as addCartItemsRejected,
} from 'b2b-apps/shop/cart/redux/cart/actionCreators';
import { fetchCartStatus } from 'b2b-apps/shop/cart/redux/cartStatus/actionCreators';
import { requestApiSaga, retryRequestApiSaga } from 'b2b-apps/shop/common/redux/api/sagas';
import { reportError } from 'b2b-apps/shop/common/redux/errors/actionCreators';
import { parseError } from 'b2b-apps/shop/common/utils/api/error';
import { MY_ACCOUNT_MESSAGE, showMyAccountToast } from 'b2b-apps/shop/myAccount/common/utils/messages';
import { Wishlist } from 'b2b-apps/shop/myAccount/wishlists/api/Wishlist';
import { AddItemsToCartType } from 'b2b-common/core/cart/api/CartTypes';
import {
  addItemsToCartFulfilled,
  addItemsToCartRejected,
  addToWishlistFulfilled,
  addToWishlistRejected,
  cloneWishlistFulfilled,
  cloneWishlistRejected,
  fetchWishlist,
  fetchWishlistFulfilled,
  fetchWishlistRejected,
  removeWishlistItemsFulfilled,
  removeWishlistItemsRejected,
} from './actionCreators';
import {
  ADD_TO_WISHLIST,
  ADD_WISHLIST_ITEMS_TO_CART,
  CLONE_WISHLIST,
  CREATE_AND_ADD_TO_WISHLIST,
  CREATE_WISHLIST,
  FETCH_WISHLIST,
  REMOVE_WISHLIST,
  REMOVE_WISHLIST_ITEM,
  REMOVE_WISHLIST_ITEMS,
  UPDATE_WISHLIST_ITEM_QUANTITY,
  UPDATE_WISHLIST_ITEM_REFERENCE,
  UPDATE_WISHLIST_NAME,
  UPDATE_WISHLIST_REFERENCE,
} from './actionTypes';
import {
  AddItemsToCartAction,
  AddToWishlistAction,
  CloneWishlistAction,
  CreateAndAddToWishlistAction,
  CreateWishlistAction,
  FetchWishlistAction,
  RemoveWishlistAction,
  RemoveWishlistItemAction,
  RemoveWishlistItemsAction,
  UpdateWishlistItemQuantityAction,
  UpdateWishlistItemReferenceAction,
  UpdateWishlistNameAction,
  UpdateWishlistReferenceAction,
} from './types';

const ERROR_ITEM_DUPLICATED = 'item_duplicated';

function* addItemsToCartSaga(action: AddItemsToCartAction):
  Generator<
    CallEffect | PutEffect,
    any,
    AxiosResponse<AddItemsToCartType>
  > {
  const { wishlistId, items } = action.payload;

  try {
    const response = yield call(requestApiSaga, Wishlist.wishlistToCart(wishlistId, items));
    yield put(addItemsToCartFulfilled(wishlistId, response.data));
    yield put(addCartItemsFulfilled(response.data));
    yield put(fetchCartStatus());
  } catch (error) {
    const parsedError = parseError(error);
    yield put(addItemsToCartRejected(wishlistId, parsedError));
    yield put(addCartItemsRejected(parsedError));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* addToWishlistSaga(action: AddToWishlistAction): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, skus, callback } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.addItems(wishlistId, skus));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successAddToWishlist);
    yield put(addToWishlistFulfilled());
    callback(true);
  } catch (error) {
    const errorData = parseError(error);

    if (
      errorData.statusCode === StatusCodes.CONFLICT
      && errorData.data?.errors?.[0] === ERROR_ITEM_DUPLICATED
    ) {
      showMyAccountToast(MY_ACCOUNT_MESSAGE.duplicationErrorAddToWishlist);
    } else {
      showMyAccountToast(MY_ACCOUNT_MESSAGE.errorAddToWishlist);
    }

    callback(true);
    yield put(addToWishlistRejected());
    yield put(reportError(action.type, error, action.payload));
  }
}

function* cloneWishlistSaga(action: CloneWishlistAction): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { wishlistId, name } = action.payload;

  try {
    const response = yield call(requestApiSaga, Wishlist.clone(wishlistId, name));
    yield put(cloneWishlistFulfilled(response.data));
  } catch (error) {
    yield put(cloneWishlistRejected(parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* createAndAddToWishlistSaga(
  action: CreateAndAddToWishlistAction,
): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { newWishlistName, skus, callback } = action.payload;

  try {
    const response = yield call(requestApiSaga, Wishlist.create({ name: newWishlistName }));
    const wishlistId = response.data.id;

    yield call(requestApiSaga, Wishlist.addItems(wishlistId, skus));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successCreateAndAddToWishlist);
    yield put(addToWishlistFulfilled());
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorCreateAndAddToWishlist);
    yield put(addToWishlistRejected());
    callback(true);
    yield put(reportError(action.type, error, action.payload));
  }
}

function* createWishlistSaga(action: CreateWishlistAction): Generator<CallEffect | PutEffect, any, void> {
  const { data, callback } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.create(data));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successCreateWishlist);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorCreateWishlist);
    callback(false);
    yield put(reportError(action.type, error, action.payload.data));
  }
}

function* fetchWishlistSaga(action: FetchWishlistAction): Generator<CallEffect | PutEffect, any, AxiosResponse> {
  const { wishlistId } = action.payload;

  try {
    const response = yield call(retryRequestApiSaga, Wishlist.fetch(wishlistId));
    yield put(fetchWishlistFulfilled(wishlistId, response.data));
  } catch (error) {
    yield put(fetchWishlistRejected(wishlistId, parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* updateWishlistNameSaga(action: UpdateWishlistNameAction): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, data, callback } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.updateName(wishlistId, data));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successUpdateWishlist);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorUpdateWishlist);
    callback(false);
    yield put(
      reportError(action.type, error, { wishlistId }),
    );
  }
}

function* updateWishlistReferenceSaga(
  action: UpdateWishlistReferenceAction,
): Generator<CallEffect | PutEffect, any, void> {
  const { reference, wishlistId } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.updateReference(wishlistId, reference));
    yield put(fetchWishlist(wishlistId));
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorUpdateWishlistReference);
    yield put(reportError(action.type, error, action.payload));
  }
}

function* removeWishlistSaga(action: RemoveWishlistAction): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, callback } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.remove(wishlistId));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successRemoveWishlist);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorRemoveWishlist);
    callback(false);
    yield put(
      reportError(action.type, error, { wishlistId }),
    );
  }
}

function* updateWishlistItemQuantitySaga(
  action: UpdateWishlistItemQuantityAction,
): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, sku, quantity } = action.payload;
  const itemQuantityData = {
    sku,
    quantity,
  };

  try {
    yield call(
      requestApiSaga,
      Wishlist.updateWishlistItemQuantity(wishlistId, itemQuantityData),
    );
    yield put(fetchWishlist(wishlistId));
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorUpdateWishlistItem);
    yield put(reportError(action.type, error, action.payload));
  }
}

function* updateWishlistItemReferenceSaga(
  action: UpdateWishlistItemReferenceAction,
): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, sku, reference } = action.payload;
  const itemReferenceData = {
    sku,
    reference,
  };

  try {
    yield call(
      requestApiSaga,
      Wishlist.updateWishlistItemReference(wishlistId, itemReferenceData),
    );
    yield put(fetchWishlist(wishlistId));
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorUpdateWishlistItem);
    yield put(reportError(action.type, error, action.payload));
  }
}

function* removeWishlistItemSaga(action: RemoveWishlistItemAction): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, sku } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.removeWishlistItem(wishlistId, sku));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successRemoveItem);
    yield put(fetchWishlist(wishlistId));
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorRemoveWishlistItem);
    yield put(reportError(action.type, error, action.payload));
  }
}

function* removeWishlistItemsSaga(action: RemoveWishlistItemsAction): Generator<CallEffect | PutEffect, any, void> {
  const { wishlistId, skus } = action.payload;

  try {
    yield call(requestApiSaga, Wishlist.removeWishlistItems(wishlistId, skus));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successRemoveItems);
    yield put(removeWishlistItemsFulfilled(wishlistId));
    yield put(fetchWishlist(wishlistId));
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorRemoveWishlistItems);
    yield put(removeWishlistItemsRejected(wishlistId, parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* wishlistSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(ADD_WISHLIST_ITEMS_TO_CART, addItemsToCartSaga),
    takeLatest(ADD_TO_WISHLIST, addToWishlistSaga),
    takeLatest(CLONE_WISHLIST, cloneWishlistSaga),
    takeLatest(CREATE_AND_ADD_TO_WISHLIST, createAndAddToWishlistSaga),
    takeLatest(CREATE_WISHLIST, createWishlistSaga),
    takeLatest(FETCH_WISHLIST, fetchWishlistSaga),
    takeLatest(UPDATE_WISHLIST_NAME, updateWishlistNameSaga),
    takeLatest(UPDATE_WISHLIST_REFERENCE, updateWishlistReferenceSaga),
    takeLatest(REMOVE_WISHLIST, removeWishlistSaga),
    takeLatest(UPDATE_WISHLIST_ITEM_QUANTITY, updateWishlistItemQuantitySaga),
    takeLatest(UPDATE_WISHLIST_ITEM_REFERENCE, updateWishlistItemReferenceSaga),
    takeLatest(REMOVE_WISHLIST_ITEM, removeWishlistItemSaga),
    takeLatest(REMOVE_WISHLIST_ITEMS, removeWishlistItemsSaga),
  ]);
}

export { wishlistSaga };
